import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Info } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";

const fmtL = (v) => {
  if (!v && v !== 0) return "0";
  const l = v / 100000;
  if (Math.abs(l) >= 100) return `${Math.round(l).toLocaleString("en-IN")} L`;
  if (Math.abs(l) >= 10) return `${l.toFixed(1)} L`;
  return `${l.toFixed(2)} L`;
};

const fmtInr = (v) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(v || 0))}`;

const CFTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const c = payload.find(p => p.dataKey === "contractual")?.payload || payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs max-w-[300px]">
      <p className="font-semibold text-slate-800 mb-1.5">{label}</p>
      <div className="space-y-0.5">
        <p className="flex justify-between gap-4"><span className="text-slate-500">Contractual</span><span className="font-semibold text-[#0066CC]">{fmtInr(c.contractual)}</span></p>
        <p className="flex justify-between gap-4"><span className="text-slate-500">Expected (DSO-adj)</span><span className="font-semibold text-[#DC7C00]">{fmtInr(c.expected)}</span></p>
      </div>
      {c.contributors?.length > 0 && (
        <>
          <p className="mt-2 mb-0.5 text-[11px] text-slate-400 uppercase tracking-wider">Top billings this week</p>
          {c.contributors.slice(0, 4).map((cn, i) => (
            <p key={i} className="text-[10.5px] text-slate-600 truncate">• {cn.name}: {fmtInr(cn.amount)}</p>
          ))}
        </>
      )}
    </div>
  );
};

export default function CashFlowForecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState(13);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collections/analytics/cash-flow-forecast?weeks=${weeks}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load forecast");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weeks]);

  const chartData = useMemo(() => (data?.weekly || []).map(w => ({
    label: w.week_label,
    contractual: w.contractual,
    expected: w.expected,
    contributors: w.contributors,
    contributors_expected: w.contributors_expected,
  })), [data]);

  const delta = data ? (data.totals.expected - data.totals.contractual) : 0;
  const deltaPct = data && data.totals.contractual > 0
    ? Math.round(((data.totals.expected - data.totals.contractual) / data.totals.contractual) * 100)
    : 0;

  return (
    <div className="space-y-4" data-testid="cash-flow-forecast">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-[#0066CC]" /> Cash Flow Forecast
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {data ? `${data.window_start} → ${data.window_end}` : "Rolling weekly projection based on active billing lines"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            {[4, 8, 13, 26].map(n => (
              <button
                key={n}
                onClick={() => setWeeks(n)}
                className={`px-2.5 py-1 rounded text-xs font-medium ${weeks === n ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`cashflow-window-${n}w`}
              >{n}w</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="cashflow-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <Card><CardContent className="p-12 flex flex-col items-center justify-center text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-2" />
          <p className="text-sm">Building forecast…</p>
        </CardContent></Card>
      ) : data ? (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contractual (bill date)</p>
              <p className="text-2xl font-bold text-[#0066CC] mt-1">₹{fmtL(data.totals.contractual)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Sum of bills falling due in window</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Expected (DSO-adjusted)</p>
              <p className="text-2xl font-bold text-[#DC7C00] mt-1">₹{fmtL(data.totals.expected)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Includes past-due bills projected to land in window</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Expected vs Contractual</p>
              <p className={`text-2xl font-bold mt-1 flex items-center gap-1 ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {delta >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                {delta >= 0 ? "+" : ""}₹{fmtL(Math.abs(delta))}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">{delta >= 0 ? "+" : ""}{deltaPct}% shift due to DSO</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Global DSO</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{data.global_dso_days} <span className="text-sm font-medium text-slate-500">days</span></p>
              <p className="text-[11px] text-slate-400 mt-1">Weighted mean lag (bill → receipt) across all history</p>
            </CardContent></Card>
          </div>

          {/* Waterfall bar chart */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Weekly Projection — Contractual vs Expected</h3>
                  <p className="text-[11px] text-slate-500">Blue = when the bill is raised · Amber = when we expect the cash to land</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtL(v)} />
                  <Tooltip content={<CFTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="contractual" name="Contractual" fill="#0066CC" radius={[3, 3, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill="#0066CC" />)}
                  </Bar>
                  <Bar dataKey="expected" name="Expected (DSO-adj)" fill="#DC7C00" radius={[3, 3, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill="#DC7C00" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detail table */}
          <Card>
            <CardContent className="p-5">
              <h3 className="font-bold text-slate-900 text-sm mb-3">Week-by-Week Detail</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="cashflow-detail-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                      <th className="text-left py-2 pr-3 font-semibold">Week</th>
                      <th className="text-right py-2 pr-3 font-semibold">Contractual</th>
                      <th className="text-right py-2 pr-3 font-semibold">Expected</th>
                      <th className="text-left py-2 pr-3 font-semibold">Top Contributors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((w, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 pr-3 font-medium text-slate-800">{w.label}</td>
                        <td className="py-2 pr-3 text-right text-[#0066CC] font-semibold">{w.contractual > 0 ? `₹${fmtL(w.contractual)}` : <span className="text-slate-300">—</span>}</td>
                        <td className="py-2 pr-3 text-right text-[#DC7C00] font-semibold">{w.expected > 0 ? `₹${fmtL(w.expected)}` : <span className="text-slate-300">—</span>}</td>
                        <td className="py-2 pr-3 text-slate-500 truncate max-w-[280px]">
                          {(w.contributors || []).slice(0, 3).map(c => c.name).join(", ") || <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* DSO by project */}
          {data.dso_by_project?.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <h3 className="font-bold text-slate-900 text-sm mb-1">Historical DSO by Project</h3>
                <p className="text-[11px] text-slate-500 mb-3 flex items-start gap-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  Average days between bill date and receipt date. Longer DSO = later expected cash. Values above 90d are worth investigating.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="dso-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500">
                        <th className="text-left py-2 pr-3 font-semibold">Project</th>
                        <th className="text-right py-2 pr-3 font-semibold">DSO (days)</th>
                        <th className="text-right py-2 pr-3 font-semibold">Receipts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.dso_by_project.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 pr-3 font-medium text-slate-800">{p.name}</td>
                          <td className={`py-2 pr-3 text-right font-semibold ${p.dso_days > 90 ? "text-red-600" : p.dso_days > 45 ? "text-amber-600" : "text-emerald-600"}`}>{p.dso_days}</td>
                          <td className="py-2 pr-3 text-right text-slate-500">{p.receipt_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
}
